/**
 * nextStep — pure "what should I do next?" logic. computeNextStep returns the
 * single highest-priority gap in the project (unit-tested). It used to render a
 * dismissible banner at the top of the workspace; that guidance now lives in the
 * Copilot panel on the right, which imports computeNextStep from here. Kept in
 * this file (and named export) so the existing test import path is unchanged.
 */

import { getActiveImpactSource, hasImpactScores, isCostOnly, DEFAULT_IMPACT_SOURCE } from '../../services/impactSource';

/** Count inventory inputs, and those not linked under the active dataset (no TRACI scores). */
function countUnlinked(project) {
  let unlinked = 0;
  let total = 0;
  const source = getActiveImpactSource(project);
  const procs = project?.processes || {};
  Object.values(procs).forEach((p) => {
    (p.lci_inputs || []).forEach((inp) => {
      if (isCostOnly(inp)) return;
      total += 1;
      if (!hasImpactScores(inp, source)) unlinked += 1;
    });
  });
  return { unlinked, total, source };
}

function hasAnyPrice(project) {
  const procs = project?.processes || {};
  return Object.values(procs).some((p) =>
    (p.products || p.output_materials || []).some((o) => Number(o.price_per_unit) > 0)
  );
}

/**
 * Decide the single most useful next action. Returns { id, message, actionLabel,
 * actionKind } or null when nothing is pending. actionKind ∈ run|link|build|add.
 */
export function computeNextStep(project, analysisResults, dirty) {
  if (!project) return null;
  const procCount = Object.keys(project.processes || {}).length;
  if (procCount === 0) {
    return { id: 'add-process', message: 'This project has no processes yet.', actionLabel: 'Add a process', actionKind: 'add' };
  }

  const { lca, tea, circularity } = analysisResults || {};
  const hasResults = !!(lca?.success || tea?.success || circularity?.success);

  const { unlinked, total, source } = countUnlinked(project);
  // Another dataset may simply have no equivalent for some inputs (GREET has no
  // carbon black or CMC, for example); the Results panel states that gap. Only prompt when
  // nothing is linked, because then LCA cannot run at all.
  const partialByDesign = source !== DEFAULT_IMPACT_SOURCE && unlinked < total;
  if (unlinked > 0 && !partialByDesign) {
    return {
      id: 'link-inputs',
      message: `${unlinked} input${unlinked === 1 ? " isn't" : "s aren't"} linked to the database — LCA can't run until ${unlinked === 1 ? 'it is' : 'they are'}.`,
      actionLabel: 'Link inputs',
      actionKind: 'link',
    };
  }

  if (!hasResults) {
    return { id: 'run', message: 'Run the analysis to see environmental impact and cost.', actionLabel: 'Run analysis', actionKind: 'run' };
  }

  if (dirty) {
    return { id: 'stale', message: 'The project changed since the last run — results may be out of date.', actionLabel: 'Re-run analysis', actionKind: 'run' };
  }

  if (tea?.success && !hasAnyPrice(project)) {
    return { id: 'prices', message: 'No selling prices set — add one to a product to see revenue & profit.', actionLabel: 'Open Build', actionKind: 'build' };
  }

  return null;
}
